import React, { useState, useEffect } from 'react';
import { DollarOutlined, ClockCircleOutlined, FireOutlined, BellOutlined, PlayCircleOutlined, UpOutlined } from '@ant-design/icons';
import { Modal, Button } from 'antd';
import moment from 'moment';
import LoadingGame from './Common/LoadingGame';
import TicTacToe from './Common/TicTacToe';
import PakistanQuiz from './Common/PakistanQuiz';
import MemoryGame from './Common/MemoryGame';
import LivestockQuiz from './Common/LivestockQuiz';
import RunningLivestock from './Common/RunningLivestock';
import MathQuiz from './Common/MathQuiz';
import MaveshiJump from './Common/MaveshiJump';
import LiveStreamPanel from './LiveAuction/LiveStreamPanel';
import { startServerTimeSync, getServerNow } from '../utils/serverTime';

// Enhanced Loading Component
const LoadingSpinner = ({
    title = "Loading Auction",
    subtitle = "Please wait while we fetch the latest data"
}) => {
    const [loadingStep, setLoadingStep] = useState(0);
    const [progress, setProgress] = useState(0);
    const loadingSteps = [
        "Initializing auction system...",
        "Connecting to real-time servers...",
        "Loading auction data...",
        "Establishing live connections...",
        "Preparing bidding interface...",
        "Setting up notifications...",
        "Configuring real-time updates...",
        "Loading lot information...",
        "Preparing sound alerts...",
        "Almost ready..."
    ];

    useEffect(() => {
        const stepInterval = setInterval(() => {
            setLoadingStep(prev => {
                if (prev < loadingSteps.length - 1) {
                    return prev + 1;
                }
                return prev;
            });
        }, 200); // Much faster transitions - reduced from 400ms to 200ms

        const progressInterval = setInterval(() => {
            setProgress(prev => {
                if (prev < 95) {
                    const newProgress = prev + Math.random() * 8; // Slower progress for more messages
                    return Math.min(newProgress, 100); // Cap at 100%
                }
                return Math.min(prev, 100); // Ensure it never exceeds 100%
            });
        }, 150);

        return () => {
            clearInterval(stepInterval);
            clearInterval(progressInterval);
        };
    }, []);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: '100vh',
            height: '100vh',
            color: '#2c3e50',
            padding: '50px 15px 15px 15px',
            position: 'relative',
            overflowY: 'auto'
        }}>
            {/* Animated Logo/Icon */}
            <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                animation: 'float 3s ease-in-out infinite',
                boxShadow: '0 15px 30px rgba(24, 144, 255, 0.3)',
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0
            }}>
                <DollarOutlined style={{ fontSize: '35px', color: 'white' }} />
                <div style={{
                    position: 'absolute',
                    top: '-3px',
                    right: '-3px',
                    width: '15px',
                    height: '15px',
                    borderRadius: '50%',
                    background: '#52c41a',
                    animation: 'pulse 2s infinite'
                }} />
            </div>

            {/* Title */}
            <h1 style={{
                fontSize: '2.2rem',
                fontWeight: '800',
                marginBottom: '8px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-1px'
            }}>
                {title}
            </h1>

            {/* Subtitle */}
            <p style={{
                fontSize: '1rem',
                marginBottom: '25px',
                textAlign: 'center',
                color: '#7f8c8d',
                maxWidth: '500px',
                fontWeight: '500'
            }}>
                {subtitle}
            </p>

            {/* Progress Bar Container */}
            <div style={{
                width: '320px',
                marginBottom: '15px',
                position: 'relative'
            }}>
                {/* Progress Bar Background */}
                <div style={{
                    width: '100%',
                    height: '10px',
                    background: 'rgba(24, 144, 255, 0.1)',
                    borderRadius: '5px',
                    overflow: 'hidden',
                    position: 'relative',
                    border: '1px solid rgba(24, 144, 255, 0.2)'
                }}>
                    {/* Progress Fill */}
                    <div style={{
                        width: `${Math.min(progress, 100)}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #1890ff, #096dd9)',
                        borderRadius: '5px',
                        transition: 'width 0.3s ease',
                        boxShadow: '0 0 15px rgba(24, 144, 255, 0.4)',
                        position: 'relative'
                    }}>
                        {/* Shimmer Effect */}
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                            animation: 'shimmer 2s infinite'
                        }} />
                    </div>
                </div>
            </div>

            {/* Progress Percentage */}
            <div style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#1890ff',
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
                {Math.round(Math.min(progress, 100))}%
            </div>

            {/* Current Step */}
            <div style={{
                background: 'white',
                padding: '12px 20px',
                borderRadius: '12px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                border: '1px solid rgba(24, 144, 255, 0.1)',
                maxWidth: '380px',
                textAlign: 'center',
                marginBottom: '20px',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                }}>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1890ff, #096dd9)',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ 
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: '#2c3e50'
                    }}>
                        {loadingSteps[loadingStep]}
                    </span>
                </div>
            </div>

            {/* Loading Steps Indicator */}
            <div style={{
                display: 'flex',
                gap: '6px',
                marginBottom: '25px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: '400px'
            }}>
                {loadingSteps.map((_, index) => (
                    <div
                        key={index}
                        style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: index <= loadingStep ? 'linear-gradient(135deg, #1890ff, #096dd9)' : 'rgba(24, 144, 255, 0.2)',
                            transition: 'all 0.3s ease',
                            animation: index === loadingStep ? 'pulse 1s infinite' : 'none',
                            boxShadow: index <= loadingStep ? '0 0 8px rgba(24, 144, 255, 0.4)' : 'none'
                        }}
                    />
                ))}
            </div>

            {/* Feature Cards */}
            <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: '480px'
            }}>
                {[
                    { icon: '🔄', text: 'Real-time bidding' },
                    { icon: '🔔', text: 'Live notifications' },
                    { icon: '⚡', text: 'Instant updates' },
                    { icon: '🎯', text: 'Smart alerts' }
                ].map((feature, index) => (
                    <div key={index} style={{
                        background: 'white',
                        padding: '10px 15px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        border: '1px solid rgba(24, 144, 255, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '110px',
                        justifyContent: 'center',
                        animation: `slideInUp 0.6s ease ${index * 0.1}s both`
                    }}>
                        <span style={{ fontSize: '1.2rem' }}>{feature.icon}</span>
                        <span style={{ 
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            color: '#2c3e50'
                        }}>
                            {feature.text}
                        </span>
                    </div>
                ))}
            </div>

            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.1); opacity: 0.8; }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-8px); }
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                @keyframes shimmer {
                    0% { left: -100%; }
                    100% { left: 100%; }
                }
                
                @keyframes slideInUp {
                    0% { 
                        opacity: 0; 
                        transform: translateY(15px); 
                    }
                    100% { 
                        opacity: 1; 
                        transform: translateY(0); 
                    }
                }
                
                .game-modal .ant-modal-header {
                    padding: 8px 16px !important;
                    border-bottom: 1px solid #f0f0f0;
                }
                
                .game-modal .ant-modal-title {
                    padding: 0 !important;
                }
                
                @media (max-width: 768px) {
                    .game-modal {
                        width: 95% !important;
                        max-width: 95% !important;
                    }
                    
                    .game-modal .ant-modal-content {
                        margin: 10px;
                    }
                    
                    .game-modal .ant-modal-header {
                        padding: 6px 12px !important;
                    }
                    
                    .game-modal .ant-modal-body {
                        padding: 8px 12px !important;
                        max-height: 85vh !important;
                    }
                }
                
                @media (max-width: 576px) {
                    .game-modal {
                        width: 100% !important;
                        max-width: 100% !important;
                        top: 0 !important;
                    }
                    
                    .game-modal .ant-modal-content {
                        margin: 0;
                        border-radius: 0;
                        height: 100vh;
                    }
                    
                    .game-modal .ant-modal-header {
                        padding: 4px 10px !important;
                    }
                    
                    .game-modal .ant-modal-body {
                        padding: 6px 10px !important;
                        max-height: calc(100vh - 110px) !important;
                    }
                }
            `}</style>
        </div>
    );
};

// Auction Countdown Component
const AuctionCountdown = ({
    auction,
    onCountdownComplete,
    ...liveStreamProps
}) => {
    const {
        liveStreamUrl = null,
        liveStreamInput = '',
        onInputChange,
        onUpdate,
        onRemove,
        updating = false,
        isOwner = false
    } = liveStreamProps;
    const [timeLeft, setTimeLeft] = useState(null);
    const [isComplete, setIsComplete] = useState(false);
    const [showGame, setShowGame] = useState(false);
    const [streamPanelExpanded, setStreamPanelExpanded] = useState(false);
    const [gameType, setGameType] = useState('catch'); // 'catch', 'tictactoe', 'pakistanquiz', 'memory', 'livestockquiz', 'mathquiz', or 'maveshijump'

    useEffect(() => {
        startServerTimeSync();
    }, []);

    useEffect(() => {
        if (!auction?.start_time) return;

        const updateCountdown = () => {
            const now = moment(getServerNow());
            // Try to parse the start time with explicit format handling
            let startTime;
            try {
                // First try parsing as ISO format
                startTime = moment(auction.start_time);
                // If it's invalid, try parsing as MySQL datetime format
                if (!startTime.isValid()) {
                    startTime = moment(auction.start_time, 'YYYY-MM-DD HH:mm:ss');
                }
                // If still invalid, try parsing as date only
                if (!startTime.isValid()) {
                    startTime = moment(auction.start_time, 'YYYY-MM-DD');
                }
            } catch (error) {
                console.error('Error parsing auction start time:', error, auction.start_time);
                return;
            }

            if (!startTime.isValid()) {
                console.error('Invalid start time format:', auction.start_time);
                return;
            }

            const duration = moment.duration(startTime.diff(now));

            if (duration.asMilliseconds() <= 0) {
                setTimeLeft(null);
                setIsComplete(true);
                if (onCountdownComplete) {
                    onCountdownComplete();
                }
                return;
            }

            setTimeLeft({
                days: Math.floor(duration.asDays()),
                hours: duration.hours(),
                minutes: duration.minutes(),
                seconds: duration.seconds()
            });
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);

        return () => clearInterval(interval);
    }, [auction?.start_time, onCountdownComplete]);

    if (isComplete) {
        return null;
    }

    const formatTimeUnit = (value) => value.toString().padStart(2, '0');

    // Helper function to parse auction start time
    const parseAuctionTime = (timeString) => {
        if (!timeString) return null;
        
        try {
            // First try parsing as ISO format
            let parsedTime = moment(timeString);
            // If it's invalid, try parsing as MySQL datetime format
            if (!parsedTime.isValid()) {
                parsedTime = moment(timeString, 'YYYY-MM-DD HH:mm:ss');
            }
            // If still invalid, try parsing as date only
            if (!parsedTime.isValid()) {
                parsedTime = moment(timeString, 'YYYY-MM-DD');
            }
            
            return parsedTime.isValid() ? parsedTime : null;
        } catch (error) {
            console.error('Error parsing auction time:', error, timeString);
            return null;
        }
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minHeight: '100vh',
            height: '100vh',
            color: '#2c3e50',
            padding: '40px 15px 30px 15px',
            position: 'relative',
            overflowY: 'auto',
            overflowX: 'hidden'
        }}>
            {/* Animated Logo/Icon */}
            <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '30px',
                animation: 'float 3s ease-in-out infinite',
                boxShadow: '0 15px 30px rgba(24, 144, 255, 0.3)',
                position: 'relative',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(24, 144, 255, 0.3)',
                overflow: 'hidden',
                flexShrink: 0
            }}>
                <FireOutlined style={{ fontSize: '40px', color: 'white' }} />
                <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: '#ff6b6b',
                    animation: 'pulse 2s infinite',
                    border: '2px solid white'
                }} />
            </div>

            {/* Title */}
            <h1 style={{
                fontSize: '1.8rem',
                fontWeight: '700',
                marginBottom: '10px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.5px'
            }}>
                {auction?.title || 'Live Auction'}
            </h1>

            {/* Subtitle */}
            <p style={{
                fontSize: '1.1rem',
                marginBottom: '12px',
                textAlign: 'center',
                color: '#7f8c8d',
                maxWidth: '500px',
                fontWeight: '500'
            }}>
                Starting Soon - Get Ready to Bid!
            </p>

            {isOwner && (
                <div style={{
                    width: '100%',
                    maxWidth: '560px',
                    marginBottom: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <Button
                        type={streamPanelExpanded ? 'default' : 'primary'}
                        icon={streamPanelExpanded ? <UpOutlined /> : <PlayCircleOutlined />}
                        onClick={() => setStreamPanelExpanded((prev) => !prev)}
                        style={{
                            borderRadius: '8px',
                            fontWeight: 600
                        }}
                    >
                        {streamPanelExpanded ? 'Hide Streaming URL' : 'Update Streaming URL'}
                    </Button>

                    {streamPanelExpanded && (
                        <LiveStreamPanel
                            liveStreamUrl={liveStreamUrl}
                            liveStreamInput={liveStreamInput}
                            onInputChange={onInputChange}
                            onUpdate={onUpdate}
                            onRemove={onRemove}
                            updating={updating}
                            isOwner={isOwner}
                            compact
                            embedCompact
                            active={streamPanelExpanded}
                        />
                    )}
                </div>
            )}

            {!isOwner && liveStreamUrl && (
                <div style={{ width: '100%', maxWidth: '560px', marginBottom: '12px' }}>
                    <LiveStreamPanel
                        liveStreamUrl={liveStreamUrl}
                        isOwner={false}
                        compact
                        embedCompact
                        active
                    />
                </div>
            )}

            {/* Waiting Message - Only show when countdown is active */}
            {timeLeft && (
                <div style={{
                    textAlign: 'center',
                    marginBottom: '16px',
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, rgba(24, 144, 255, 0.1) 0%, rgba(82, 196, 26, 0.1) 100%)',
                    borderRadius: '8px',
                    border: '1px solid rgba(24, 144, 255, 0.2)',
                    maxWidth: '500px',
                    margin: '0 auto 16px auto'
                }}>
                    <p style={{
                        fontSize: '13px',
                        color: '#1890ff',
                        fontWeight: '500',
                        margin: 0,
                        lineHeight: '1.4'
                    }}>
                        ⏳ While you wait, play our fun games! 🎮
                    </p>
                </div>
            )}

            {/* Game Buttons - Only show when countdown is active */}
            {timeLeft && (
                <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {/* <button
                    onClick={() => {
                        setShowGame(true);
                        setGameType('catch');
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        border: 'none',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(82, 196, 26, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.3)';
                    }}
                >
                    🎯 Catch Maveshi
                </button>  */}
                <button
                    onClick={() => {
                        setShowGame(true);
                        setGameType('pakistanquiz');
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                        border: 'none',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(114, 46, 209, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(114, 46, 209, 0.3)';
                    }}
                >
                    🇵🇰 Pakistan Quiz
                </button>
                  {/* <button
                    onClick={() => {
                        setShowGame(true);
                        setGameType('tictactoe');
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        border: 'none',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.3)';
                    }}
                >
                    ⭕ Tic-Tac-Hoof
                </button> 
                <button
                    onClick={() => {
                        setShowGame(true);
                        setGameType('memory');
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                        border: 'none',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(255, 107, 107, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.3)';
                    }}
                >
                    🧠 Maveshi Memory
                </button>  */}
                <button
                    onClick={() => {
                        setShowGame(true);
                        setGameType('livestockquiz');
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        border: 'none',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(82, 196, 26, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.3)';
                    }}
                >
                    🐄 مویشی پالنے کا کویز
                </button>
                {/* <button
                    onClick={() => {
                        setShowGame(true);
                        setGameType('mathquiz');
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
                        border: 'none',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(250, 140, 22, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(250, 140, 22, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(250, 140, 22, 0.3)';
                    }}
                >
                    🧮 Math Quiz
                </button>
                <button
                    onClick={() => {
                        setShowGame(true);
                        setGameType('maveshijump');
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #13c2c2 0%, #08979c 100%)',
                        border: 'none',
                        color: 'white',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(19, 194, 194, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(19, 194, 194, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(19, 194, 194, 0.3)';
                    }}
                >
                    🦘 Maveshi Jump
                </button>  */}
                </div>
            )}

            {/* Game Modal */}
            <Modal
                title={
                    <div style={{ textAlign: 'center', padding: gameType === 'tictactoe' ? '4px 0' : '8px 0' }}>
                        <span style={{ fontSize: gameType === 'tictactoe' ? '18px' : '22px', fontWeight: 'bold', color: '#1890ff' }}>
                            {gameType === 'catch' ? '🎮 Catch Maveshi Game' : 
                             gameType === 'tictactoe' ? '⭕ Tic-Tac-Hoof Game' : 
                             gameType === 'memory' ? '🧠 Maveshi Memory Game' :
                             gameType === 'livestockquiz' ? '🐄 مویشی پالنے کا علم کا کویز' :
                             gameType === 'mathquiz' ? '🧮 Math Quiz Challenge' :
                             gameType === 'maveshijump' ? '🦘 Maveshi Jump' :
                             '🇵🇰 Pakistan General Knowledge Quiz'}
                        </span>
                    </div>
                }
                open={showGame}
                onCancel={() => {
                    // Stop any ongoing speech when modal closes
                    if (window.speechSynthesis) {
                        window.speechSynthesis.cancel();
                    }
                    setShowGame(false);
                    setGameType('catch'); // Reset to default
                }}
                footer={null}
                width={700}
                // width={gameType === 'catch' ? 800 : gameType === 'pakistanquiz' ? 700 : 600}
                centered
                destroyOnClose={true}
                style={{
                    top: 20
                }}
                className="game-modal"
                bodyStyle={{
                    padding: gameType === 'tictactoe' ? '8px 16px' : gameType === 'memory' ? '12px 16px' : '16px',
                    maxHeight: '88vh',
                    overflowY: (gameType === 'tictactoe' || gameType === 'livestockquiz' || gameType === 'pakistanquiz' || gameType === 'mathquiz' || gameType === 'maveshijump') ? 'auto' : 'hidden',
                    overflowX: 'hidden'
                }}
            >
                {gameType === 'catch' ? (
                    <LoadingGame onGameComplete={(finalScore) => {
                        console.log('Game completed with score:', finalScore);
                    }} />
                ) : gameType === 'tictactoe' ? (
                    <TicTacToe onGameComplete={(finalScore) => {
                        console.log('Game completed with score:', finalScore);
                    }} />
                ) : gameType === 'memory' ? (
                    <MemoryGame onGameComplete={(finalScore) => {
                        console.log('Game completed with score:', finalScore);
                    }} />
                ) : gameType === 'livestockquiz' ? (
                    <LivestockQuiz onGameComplete={(finalScore) => {
                        console.log('Game completed with score:', finalScore);
                    }} />
                ) : gameType === 'mathquiz' ? (
                    <MathQuiz onGameComplete={(finalScore) => {
                        console.log('Game completed with score:', finalScore);
                    }} />
                ) : gameType === 'maveshijump' ? (
                    <MaveshiJump onGameComplete={(finalScore) => {
                        console.log('Game completed with score:', finalScore);
                    }} />
                ) : (
                    <PakistanQuiz onGameComplete={(finalScore) => {
                        console.log('Game completed with score:', finalScore);
                    }} />
                )}
            </Modal> 

            {/* Countdown Timer */}
            {timeLeft && (
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '30px 40px',
                    borderRadius: '20px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    backdropFilter: 'blur(10px)',
                    marginBottom: '30px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        marginBottom: '20px'
                    }}>
                        <ClockCircleOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                        <span style={{
                            fontSize: '1.2rem',
                            fontWeight: '600',
                            color: '#2c3e50'
                        }}>
                            Auction Starts In
                        </span>
                    </div>

                    <div style={{
                        display: 'flex',
                        gap: '15px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        {timeLeft.days > 0 && (
                            <div style={{
                                textAlign: 'center',
                                minWidth: '80px'
                            }}>
                                <div style={{
                                    fontSize: '2.5rem',
                                    fontWeight: 'bold',
                                    color: '#1890ff',
                                    lineHeight: 1
                                }}>
                                    {formatTimeUnit(timeLeft.days)}
                                </div>
                                <div style={{
                                    fontSize: '0.9rem',
                                    color: '#7f8c8d',
                                    fontWeight: '500'
                                }}>
                                    Days
                                </div>
                            </div>
                        )}
                        <div style={{
                            textAlign: 'center',
                            minWidth: '80px'
                        }}>
                            <div style={{
                                fontSize: '2.5rem',
                                fontWeight: 'bold',
                                color: '#1890ff',
                                lineHeight: 1
                            }}>
                                {formatTimeUnit(timeLeft.hours)}
                            </div>
                            <div style={{
                                fontSize: '0.9rem',
                                color: '#7f8c8d',
                                fontWeight: '500'
                            }}>
                                Hours
                            </div>
                        </div>
                        <div style={{
                            textAlign: 'center',
                            minWidth: '80px'
                        }}>
                            <div style={{
                                fontSize: '2.5rem',
                                fontWeight: 'bold',
                                color: '#1890ff',
                                lineHeight: 1
                            }}>
                                {formatTimeUnit(timeLeft.minutes)}
                            </div>
                            <div style={{
                                fontSize: '0.9rem',
                                color: '#7f8c8d',
                                fontWeight: '500'
                            }}>
                                Minutes
                            </div>
                        </div>
                        <div style={{
                            textAlign: 'center',
                            minWidth: '80px'
                        }}>
                            <div style={{
                                fontSize: '2.5rem',
                                fontWeight: 'bold',
                                color: '#1890ff',
                                lineHeight: 1
                            }}>
                                {formatTimeUnit(timeLeft.seconds)}
                            </div>
                            <div style={{
                                fontSize: '0.9rem',
                                color: '#7f8c8d',
                                fontWeight: '500'
                            }}>
                                Seconds
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Auction Info */}
            <div style={{
                background: 'rgba(24, 144, 255, 0.1)',
                padding: '20px 30px',
                borderRadius: '15px',
                border: '1px solid rgba(24, 144, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                marginBottom: '30px',
                textAlign: 'center'
            }}>
                <div style={{
                    color: '#2c3e50',
                    fontSize: '1rem',
                    fontWeight: '600',
                    marginBottom: '10px'
                }}>
                    📅 {parseAuctionTime(auction?.start_time)?.format('dddd, MMMM D, YYYY') || 'Date not available'}
                </div>
                <div style={{
                    color: '#7f8c8d',
                    fontSize: '0.9rem'
                }}>
                    🕐 {parseAuctionTime(auction?.start_time)?.format('h:mm A') || 'Time not available'}
                </div>
            </div>

            {/* Feature Cards */}
            <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: '480px'
            }}>
                {[
                    { icon: '🔥', text: 'Live bidding' },
                    { icon: '⚡', text: 'Real-time updates' },
                    { icon: '🔔', text: 'Smart notifications' },
                    { icon: '🎯', text: 'Auto-bidding' }
                ].map((feature, index) => (
                    <div key={index} style={{
                        background: 'rgba(24, 144, 255, 0.1)',
                        padding: '12px 18px',
                        borderRadius: '12px',
                        border: '1px solid rgba(24, 144, 255, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '120px',
                        justifyContent: 'center',
                        animation: `slideInUp 0.6s ease ${index * 0.1}s both`,
                        backdropFilter: 'blur(10px)'
                    }}>
                        <span style={{ fontSize: '1.3rem' }}>{feature.icon}</span>
                        <span style={{ 
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: '#2c3e50'
                        }}>
                            {feature.text}
                        </span>
                    </div>
                ))}
            </div>

            {/* Running Livestock Animation */}
            {/* <RunningLivestock /> */}

            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.1); opacity: 0.8; }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-8px); }
                }
                
                @keyframes slideInUp {
                    0% { 
                        opacity: 0; 
                        transform: translateY(15px); 
                    }
                    100% { 
                        opacity: 1; 
                        transform: translateY(0); 
                    }
                }
                
                .game-modal .ant-modal-header {
                    padding: 8px 16px !important;
                    border-bottom: 1px solid #f0f0f0;
                }
                
                .game-modal .ant-modal-title {
                    padding: 0 !important;
                }
                
                @media (max-width: 768px) {
                    .game-modal {
                        width: 95% !important;
                        max-width: 95% !important;
                    }
                    
                    .game-modal .ant-modal-content {
                        margin: 10px;
                    }
                    
                    .game-modal .ant-modal-header {
                        padding: 6px 12px !important;
                    }
                    
                    .game-modal .ant-modal-body {
                        padding: 8px 12px !important;
                        max-height: 85vh !important;
                    }
                }
                
                @media (max-width: 576px) {
                    .game-modal {
                        width: 100% !important;
                        max-width: 100% !important;
                        top: 0 !important;
                    }
                    
                    .game-modal .ant-modal-content {
                        margin: 0;
                        border-radius: 0;
                        height: 100vh;
                    }
                    
                    .game-modal .ant-modal-header {
                        padding: 4px 10px !important;
                    }
                    
                    .game-modal .ant-modal-body {
                        padding: 6px 10px !important;
                        max-height: calc(100vh - 110px) !important;
                    }
                }
            `}</style>
        </div>
    );
};

export { LoadingSpinner, AuctionCountdown };
export default LoadingSpinner; 